import {
  OverviewContainer,
  OverviewCardInfo,
  Label,
  OverviewSegmentInfo,
  ViewDetails,
  AgentTeamSelectorWrapper,
  DescriptionKnowledgeBaseContainer,
  DescriptionWrapper,
  KnowledgeBaseWrapper,
  OverviewKnowledgeBaseContainer,
  CustomGenericListStyle,
  AgentLabelContainer,
  ToggleDescriptionContainer,
  ToggleRow,
  ToggleLabel,
  ToggleSubtitle,
  ToggleDescription
} from "../Marketing.styled";
import { AgentTeamSelector } from "../../../../AsistantManagement/components/AgentTeamSelector";
import { useStore } from "../../../../../../hooks";
import { observer } from "mobx-react";
import SidebarKnowledgeBaseCard from "../../../../HomePage/components/SidebarKnowledgeBaseCard";
import { useMediaQueries } from "../../../../../../utils/mediaQueries";
import { IconButton } from "../../../../../../elements";
import EditMarketingModal from "../Components/Modal/EditMarketingModal";
import StatusBadge from "../../../../../../elements/StatusBadge";
import RetryComponent from "../Components/Retry/RetryComponent";
import AccordionCard from "../Components/AccordionCard/AccordionCard";
import {MarketingStatus, MarketingTab, PermissionCodes} from "../../../../../../utils/enums";
import Spinner from "../../../../../../components/common/Spinner";
import {useEffect, useMemo, useState} from "react";
import FormControlLabel from '@mui/material/FormControlLabel';
import { StyledSwitch } from '../Tabs/AdvancedSettings.styled';
import Icon from "../../../../../../elements/icon/Icon";
import {useUserCheckPermissions} from "../../../../../../hooks/useUserCheckPermissions";

const Overview = observer(() => {
  const { authStore, marketingStore } = useStore();
  const { isMobile, isTablet } = useMediaQueries();
  const [agents, setAgents] = useState([]);
  const [autoMode, setAutoMode] = useState(true);
  const {checkPermission} = useUserCheckPermissions();
  
  if (!marketingStore.currentCampaign) {
    return <Spinner />;
  }
  
  const formatDate = (date) => {
    return date ? date.format('MM/DD/YYYY - HH:mm') : '---';
  }

  useEffect(() => {
    marketingStore.setAgents(authStore.agents);
    setAgents(authStore.agents);
  }, [authStore.agents]);

  const filterConsumerAgents = useMemo(() => {
    // For the new agent API, all agents are available for marketing campaigns
    // The old scope-based filtering is no longer applicable
    return agents.filter((agent) => {
      // Filter out concierge agents and only show active agents
      const isConcierge = agent.domain === 'concierge';
      const isActive = agent.status === 'active' || agent.status === 'Active' || !agent.status;
      return !isConcierge && isActive;
    });
  }, [agents]);

  const handleViewDetails = () => {
    marketingStore.onChangeTab(MarketingTab.Segments)
  };

  useEffect(() => {
    const currentCampaign = marketingStore.currentCampaign;
    if(!currentCampaign) return;

    if(currentCampaign.metadata.mode) {
      setAutoMode(currentCampaign.metadata.mode === 'auto');
    } else {
      setAutoMode(currentCampaign.status === MarketingStatus.RUNNING)
    }
  }, [marketingStore.currentCampaign]);

  useEffect(() => {
    marketingStore.onChangeAttributeCampaign('metadata', { ...marketingStore.currentCampaign.metadata, mode: autoMode ? 'auto' : 'manual' });
  }, [autoMode])

  return (
    <>
      <EditMarketingModal />
      <OverviewContainer isMobile={isMobile}>
        <OverviewCardInfo>
          <div>
            <Label>Marketing Information</Label>
            {
              (marketingStore.currentCampaign.status === MarketingStatus.DRAFT &&
               checkPermission(PermissionCodes.MARKETING_INFO,'UPDATE'))
              && <IconButton
                icon="editBlue"
                width="15px"
                height="15px"
                onClick={() => marketingStore.onOpenModalEditCampaign()}
              />
            }
          </div>
          <span>
          <Label>Name: </Label>{marketingStore.currentCampaign.name}
        </span>
          <span>
          <Label>Description: </Label> {marketingStore.currentCampaign.description}
        </span>
          <span>
          <Label>
            Status: <StatusBadge status={marketingStore.currentCampaign.status.toLowerCase()}>
            {marketingStore.currentCampaign.status}
            </StatusBadge>
          </Label>
        </span>
          <span>
          <Label>Start date: </Label> {formatDate(marketingStore.currentCampaign.startDate)}
        </span>
          <span>
          <Label>End date: </Label> {formatDate(marketingStore.currentCampaign.endDate)}
        </span>
        </OverviewCardInfo>

        {checkPermission(PermissionCodes.RETRIES, "UPDATE") && (
          <AccordionCard title="Campaign Retry">
            <RetryComponent />
          </AccordionCard>
        )}

        <OverviewSegmentInfo>
          <Label>Segment</Label>
          <ViewDetails onClick={handleViewDetails}>View details</ViewDetails>
        </OverviewSegmentInfo>

        {checkPermission(PermissionCodes.AGENT_SELECTION, "UPDATE") && (
            <OverviewCardInfo>
              <AgentLabelContainer>
                <Label>Agent</Label>
                <ToggleRow>
                  <ToggleLabel>{autoMode ? 'Auto Mode' : 'Manual Mode'}</ToggleLabel>
                  <FormControlLabel
                      control={
                        <StyledSwitch
                            disabled={marketingStore.currentCampaign.status !== MarketingStatus.DRAFT}
                            checked={!autoMode}
                            onChange={() => setAutoMode((prev) => !prev)}
                        />
                      }
                      label=""
                      style={{ marginRight: 0 }}
                  />
                </ToggleRow>
              </AgentLabelContainer>
              <ToggleDescriptionContainer>
                <ToggleSubtitle> <Icon name="info" width="14px" height="14px" /> <span>{autoMode ? 'Automated Mode' : 'Manual Mode'}</span></ToggleSubtitle>
                <ToggleDescription>
                  {autoMode
                      ? "In Automated Mode, agents operate independently. You'll lose the ability to manually intervene—responses are sent instantly without review or approval."
                      : "In Manual Mode, you have full control over each agent response. You can review, approve, correct, or regenerate answers before they're delivered—ideal for fine-tuning tone, accuracy, or personalization."}
                </ToggleDescription>
              </ToggleDescriptionContainer>
              <AgentTeamSelectorWrapper isMobile={isMobile}>
                <AgentTeamSelector
                    agentName={marketingStore.currentAgent?.name}
                    status={marketingStore.currentAgent?.active}
                    agents={filterConsumerAgents}
                    scope={marketingStore.currentAgent?.scope}
                    onChange={(agentSelected) => marketingStore.selectCurrentAgent(agentSelected)}
                    agentImageUrl={marketingStore.currentAgent?.photo_url}
                />
              </AgentTeamSelectorWrapper>
              {marketingStore.currentAgent && (
                  <DescriptionKnowledgeBaseContainer>
                    {marketingStore.currentAgent.job_title && (
                        <DescriptionWrapper>
                          <Label>Description</Label>
                          <span>{marketingStore.currentAgent.job_title}</span>
                        </DescriptionWrapper>
                    )}
                    <KnowledgeBaseWrapper>
                      {marketingStore.currentAgent.knowledge_base &&
                          marketingStore.currentAgent.knowledge_base.length > 0 && (
                              <>
                                <Label>Knowledge base</Label>
                                <OverviewKnowledgeBaseContainer
                                    isTablet={isTablet}
                                    isMobile={isMobile}
                                >
                                  {marketingStore.currentAgent.knowledge_base.map((item) => (
                                      <SidebarKnowledgeBaseCard
                                          key={item}
                                          knowledgeBase={[item]}
                                      />
                                  ))}
                                </OverviewKnowledgeBaseContainer>
                              </>
                          )}
                    </KnowledgeBaseWrapper>
                  </DescriptionKnowledgeBaseContainer>
              )}
            </OverviewCardInfo>
        )}
        {isMobile && <CustomGenericListStyle />}
      </OverviewContainer>
    </>
  );
});

export default Overview;
